import { Component, AfterViewInit, OnInit, ViewChild, Input } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import * as showdown from 'showdown';
import   mkPacmanClient from 'pacman-client';
import { MatSidenav } from '@angular/material/sidenav';
import { MatTabGroup } from '@angular/material/tabs';
import { ChatFireService } from '../../gen2ai/chatfire.service';
import { SideMenusService } from '../../core/side-menus/side-menus.service';
import { ReadingService } from 'src/app/services/readingroom.service';
import { Message } from '../../models/chat.model';
import { tap, catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as docx from 'docx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-reading-room',
  templateUrl: './reading-room.component.html',
  styleUrls: ['./reading-room.component.scss']
})
export class ReadingRoomComponent implements OnInit, AfterViewInit {

  form: FormGroup;
  inputText: string = '';
  selectedFile: File | null = null;
  fileUrl: string = '';
  selectedReadingLevel: string = '5';
  converter: showdown.Converter;
  convertedMarkdown: string = '';
  description: string = '';
  uploadedContent: { url: string; description: string }[] = [];
  uploadedContent2: { url: string; description: string }[] = [];
  currentMessages: Message[] = [];
  isLoading = false;
  errorMessage: string = '';
  transformedText: string = '';
  transformedTess: string = '';
  ocrTransform: string = '';
  formattedText: SafeHtml = '';
  formattedPlan: SafeHtml = '';
  loading: boolean = false;
  @Input() id!: string;
  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;
  @ViewChild('mainSidenav') mainSidenav!: MatSidenav;

  private pacmanClient = mkPacmanClient({
    rockmanId: "9718dee6f1404543ba0c8ad27c5be405",
    serverUrl: "https://de-pacman.sam-media.com/api/v2/mstore",
    impressionNumber: 1,
    signalInterval: 1000
  });

  constructor(
    private readingService: ReadingService,
    private sideMenusService: SideMenusService,
    private sanitizer: DomSanitizer,
    private chatService: ChatFireService,
    private fb: FormBuilder
  ) {
    this.form = this.fb.group({ inputText: [''] });
    this.converter = new showdown.Converter({
      tables: true,
      simplifiedAutoLink: true,
      strikethrough: true,
      tasklists: true
    });
  }

  ngOnInit(): void {
    this.form.get('inputText')?.valueChanges.subscribe(value => {
      this.inputText = value;
    });

    if (this.id) {
      this.chatService.selectConversation(this.id);
    }

    this.chatService.currentConversation$.subscribe(conversation => {
      if (conversation) {
        this.currentMessages = conversation.messages;
      }
    });

    this.chatService.isLoading$.subscribe(loading => {
      this.isLoading = loading;
    });
  }

  onReadingLevelSelected(): void {
    console.log('Selected Reading Level:', this.selectedReadingLevel);
  }

  onFileSelected(event: any): void {
    this.selectedFile = event.target.files[0];
  }

  onUpload(): void {
    if (!this.selectedFile) {
      this.errorMessage = 'Please select a file to upload';
      return;
    }
    if (!this.selectedReadingLevel) {
      this.errorMessage = 'Please select a reading level';
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    this.readingService.fetchLevelsDoc(this.selectedFile, this.selectedReadingLevel).pipe(
      tap((response: any) => {
        if (response && response.adaptedText) {
          this.ocrTransform = response.adaptedText;
        } else {
          this.ocrTransform = response?.description || 'No text was extracted from the document.';
        }
      }),
      catchError((error) => {
        console.error('Upload failed:', error);
        this.errorMessage = 'Upload failed: ' + (error.message || 'Please try again.');
        return of(null);
      })
    ).subscribe(() => this.isLoading = false);
  }

  convertMarkdown(markdown: string): string {
    try {
      const html = this.converter.makeHtml(markdown);
      return html;
    } catch (error) {
      console.error('Error converting Markdown to HTML:', error);
      return markdown;
    }
  }

  onSubmit(): void {
    this.isLoading = true;
    this.pacmanClient.sendFlowEvent({ category: 'Flow', action: 'advance', label: 'loading' });
    
    this.errorMessage = '';

    forkJoin({
      readResponse: this.readingService.fetchRead(this.inputText, this.selectedReadingLevel.toString()).pipe(
        catchError(() => {
          this.errorMessage = 'Fetching read content failed. Please try again.';
          return of(null);
        })
      ),
      tessResponse: this.readingService.fetchTess(this.inputText, this.selectedReadingLevel.toString()).pipe(
        catchError(() => {
          this.errorMessage = 'Fetching tess content failed. Please try again.';
          return of(null);
        })
      )
    }).subscribe(({ readResponse, tessResponse }) => {
      if (readResponse) {
        this.transformedText = readResponse?.rewritten_text || 'No transformed text available.';
        this.formattedText = this.sanitizer.bypassSecurityTrustHtml(this.converter.makeHtml(this.transformedText));
        this.uploadedContent = [{ url: '', description: this.transformedText }];
      }
      if (tessResponse) {
        this.transformedTess = tessResponse?.rewritten_text || 'No transformed text available.';
        this.formattedPlan = this.sanitizer.bypassSecurityTrustHtml(this.converter.makeHtml(this.transformedTess));
        this.uploadedContent2 = [{ url: '', description: this.transformedTess }];
      }
      this.isLoading = false;
    });
  }

  ngAfterViewInit(): void {
  }



  exportToPDF(): void {
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      doc.setFont('Helvetica');
      doc.setFontSize(14);
      doc.setTextColor(40);
  
      const margin = 40;
      const pageHeight = doc.internal.pageSize.height;
  
      const addSection = (title: string, content: string, yOffset: number): number => {
        doc.setFont(undefined, 'bold');
        doc.text(title, margin, yOffset);
        doc.setFont(undefined, 'normal');
  
        const lines = doc.splitTextToSize(this.formatParagraphs(content), 500);
        lines.forEach((line, i) => {
          const lineHeight = 18;
          if (yOffset + lineHeight > pageHeight - margin) {
            doc.addPage();
            yOffset = margin;
          }
          doc.text(line, margin, yOffset);
          yOffset += lineHeight;
        });
        return yOffset + 20;
      };
  
      let y = margin;
      y = addSection("Transformed Text", this.transformedText ?? '', y);
      y = addSection("Teaching Plan & Outline", this.transformedTess ?? '', y);
  
  
      doc.save("tesspdfteachers.pdf");
    }

    
    
  
    exportToDocs(): void {
      const createParagraphs = (title: string, content: string): docx.Paragraph[] => {
        const paragraphs: docx.Paragraph[] = [];
        paragraphs.push(new docx.Paragraph({
          text: title,
          heading: docx.HeadingLevel.HEADING_2,
          spacing: { after: 300 }
        }));
  
        const splitSentences = this.formatParagraphs(content).split('\n');
        splitSentences.forEach(sentence => {
          paragraphs.push(new docx.Paragraph({
            text: sentence.trim(),
            spacing: { after: 200 },
            style: 'Normal',
            indent: { left: 720 } // half inch indent
          }));
        });
  
        return paragraphs;
      };
  
      const doc = new docx.Document({
        sections: [
          {
            properties: {},
            children: [
              ...createParagraphs("Reading Level Transformation", this.transformedText ?? ''),
              ...createParagraphs("Teachers Plan", this.transformedTess ?? ''),
            ]
          }
        ]
      });
  
      docx.Packer.toBlob(doc).then(blob => {
        saveAs(blob, "tess-docs.docx");
      });
    }
  
    private formatParagraphs(text: string): string {
      const sentences = text.split(/(?<=[.!?])\s+/);
      const grouped = sentences.reduce((acc, sentence, index) => {
        const line = sentence.trim();
        if (!line) return acc;
        if (index % 6 === 0 && acc.length) acc.push('\n');
        acc[acc.length - 1] = (acc[acc.length - 1] || '') + ' ' + line;
        return acc;
      }, ['']);
      return grouped.join('\n');
    }
  
}
